import * as React from 'react';
import { DocumentHead, LoadingIndicator, PageTemplate } from 'lib/components';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';
import { History } from 'history';
import { text } from 'lib/utils';
import { useVideoAppearanceStore, VideoData } from 'lib/hooks';
import { PreLoader } from '.';
import {
  GLOBAL_ACTIONS,
  handleDeleteVideo,
  useGlobalDispatch,
  VideoDeletesContextProvider,
} from 'lib/context';
import { successToast } from 'lib/components/toasts/success';
import { NotFound } from 'app/pages/notFound/NotFound';
import { errorToast } from 'lib/components/toasts/error';
import { DeleteVideoModal } from 'lib/components/modal/DeleteVideoModal';
import { getVideo } from 'lib/api/videosApi';

interface MatchParams {
  videoId: string;
  view: string;
}

export const VideoDetails = withRouter(
  (props: RouteComponentProps<MatchParams>) => {
    const { videoId, view } = props.match.params;
    const { history } = props;
    const [videoIdState, setVideoIdState] = React.useState(videoId);
    const { dispatch } = useGlobalDispatch();
    const [showVideoDeleteModal, setShowVideoDeleteModal] = React.useState(
      false
    );
    const [loading, setLoading] = React.useState(true);
    const [reloadVideoDetails, setReloadVideoDetails] = React.useState(false);
    const [videoDetails, setVideoDetails] = React.useState({} as VideoData);
    const { DELETE_VIDEOS_ERROR } = GLOBAL_ACTIONS;

    const {
      data: videoAppearanceData,
      loading: videoAppearanceLoading,
    } = useVideoAppearanceStore();

    const deleteVideo = async (videoId: string, history: History) => {
      try {
        let delete_success = false;

        await handleDeleteVideo({
          videoIds: [videoId],
          dispatch: obj => {
            if (obj.type === DELETE_VIDEOS_ERROR) {
              errorToast({
                title: obj.error || 'Something went wrong',
              });
            } else {
              successToast({
                title: `Video${
                  videoId.length > 1 ? 's' : ''
                } successfully moved to recently deleted bin`,
              });
              dispatch(obj);
              delete_success = true;
            }
          },
        });
        closeVideoDeleteModal();
        if (delete_success) history.push('/');
      } catch (err) {
        console.log('err', err);
      }
    };

    const openVideoDeleteModal = () => {
      setShowVideoDeleteModal(true);
    };

    const closeVideoDeleteModal = () => {
      setShowVideoDeleteModal(false);
    };

    const renderDeleteVideoModal = () => {
      return (
        // eslint-disable-next-line react/jsx-pascal-case
        <DeleteVideoModal
          content={text.deleteModal.deleteModalBody}
          handleModalClose={closeVideoDeleteModal}
          handleModalSubmit={() => deleteVideo(videoIdState, history)}
          isCompanyVideo={false}
        />
      );
    };

    React.useEffect(() => {
      const getVideoDetails = async () => {
        setLoading(true);
        const video = await getVideo(videoId).catch(() => null);
        setVideoDetails(video);
        setLoading(false);
      };
      getVideoDetails();
    }, [reloadVideoDetails]);

    if (loading || videoAppearanceLoading || !videoAppearanceData) {
      return <LoadingIndicator isLoading={true} />;
    }

    if (!videoDetails) {
      return <NotFound />;
    }

    return (
      <VideoDeletesContextProvider>
        <DocumentHead title='Video Details' />
        <PageTemplate
          modal={showVideoDeleteModal ? [renderDeleteVideoModal()] : [null]}
          main={
            <PreLoader
              videoDetails={videoDetails}
              setReloadVideoDetails={setReloadVideoDetails}
              videoAppearanceData={videoAppearanceData}
              view={view}
              openVideoDeleteModal={openVideoDeleteModal}
              setVideoIdState={setVideoIdState}
            />
          }
        />
      </VideoDeletesContextProvider>
    );
  }
);
